/**
 * Image Conversion Utilities
 * Provides WebP conversion and optimization for all image uploads
 */

export interface ConversionOptions {
  quality?: number; // WebP quality (0-1), default 0.9
  maxWidth?: number; // Maximum width in pixels
  maxHeight?: number; // Maximum height in pixels
}

export interface ConversionResult {
  file: File;
  originalSize: number;
  newSize: number;
  compressionRatio: number;
  originalFormat: string;
  newFormat: string;
}

/**
 * Convert any image to WebP format with optional resizing and quality control
 */
export async function convertToWebP(
  file: File, 
  options: ConversionOptions = {}
): Promise<ConversionResult> {
  const { quality = 0.9, maxWidth, maxHeight } = options;
  
  console.log('[ImageConversion] Starting conversion:', {
    originalName: file.name,
    originalSize: file.size,
    originalType: file.type,
    quality,
    maxWidth,
    maxHeight
  });

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    img.onload = () => {
      try {
        // Calculate optimal dimensions
        let { width, height } = calculateOptimalDimensions(
          img.width,
          img.height,
          maxWidth,
          maxHeight
        );

        // Set canvas size
        canvas.width = width;
        canvas.height = height;

        // Draw image with high quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to WebP
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to convert image to WebP'));
              return;
            }

            // Create new File with WebP extension
            const originalName = file.name.replace(/\.[^/.]+$/, '');
            const webpFile = new File([blob], `${originalName}.webp`, {
              type: 'image/webp',
              lastModified: Date.now(),
            });

            const compressionRatio = ((file.size - blob.size) / file.size) * 100;

            console.log('[ImageConversion] Conversion complete:', {
              originalSize: file.size,
              newSize: blob.size,
              compressionRatio: `${compressionRatio.toFixed(1)}%`,
              dimensions: `${width}x${height}`
            });

            resolve({
              file: webpFile,
              originalSize: file.size,
              newSize: blob.size,
              compressionRatio,
              originalFormat: file.type,
              newFormat: 'image/webp'
            });
          },
          'image/webp',
          quality
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for conversion'));
    };

    // Start loading the image
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Calculate optimal dimensions while maintaining aspect ratio
 */
function calculateOptimalDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth?: number,
  maxHeight?: number
): { width: number; height: number } {
  let width = originalWidth;
  let height = originalHeight;

  // Apply max width constraint
  if (maxWidth && width > maxWidth) {
    const ratio = maxWidth / width;
    width = maxWidth;
    height = Math.round(height * ratio);
  }

  // Apply max height constraint
  if (maxHeight && height > maxHeight) {
    const ratio = maxHeight / height;
    height = maxHeight;
    width = Math.round(width * ratio);
  }

  return { width, height };
}

/**
 * Check if a file type supports WebP conversion
 */
export function isConvertibleToWebP(file: File): boolean {
  const convertibleTypes = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/gif',
    'image/bmp',
    'image/tiff',
    'image/svg+xml'
  ];
  
  return convertibleTypes.includes(file.type.toLowerCase());
}

/**
 * Get optimal conversion settings based on image type and use case
 */
export function getOptimalConversionSettings(
  file: File,
  useCase: 'profile' | 'workbook' | 'cover' = 'workbook'
): ConversionOptions {
  const baseSettings: Record<string, ConversionOptions> = {
    profile: {
      quality: 0.9,
      maxWidth: 512,
      maxHeight: 512
    },
    workbook: {
      quality: 0.85,
      maxWidth: 1920,
      maxHeight: 1080
    },
    cover: {
      quality: 0.9,
      maxWidth: 1920,
      maxHeight: 1080
    }
  };

  const settings = baseSettings[useCase] || baseSettings.workbook;

  // For very large files, be more aggressive with compression
  if (file.size > 5 * 1024 * 1024) { // > 5MB
    settings.quality = Math.max(0.7, (settings.quality || 0.85) - 0.1);
  }

  return settings;
} 